import { useCallback, useRef } from 'react';
import {docInfo} from "../../../utils/types.ts";

type Params = {
    doctors: docInfo[];
    orders: any[];
    setValues: React.Dispatch<React.SetStateAction<any>>;
    checkAllMedsSelected: (allSelectedMeds: string[]) => void;
};

export default function useSyncedMedSelections({
                                                   doctors,
                                                   orders,
                                                   setValues,
                                                   checkAllMedsSelected,
                                               }: Params) {
    const hasInitialized = useRef(false);

    const updateOrderData = useCallback(
        (medName: string, docName: string) => {
            setValues((prevValues: any) => ({
                ...prevValues,
                orders: prevValues.orders.map((ord: any) => {
                    if (medName === '') {
                        return ord.medicationName === medName
                            ? { ...ord, doctorName: '' }
                            : ord;
                    }

                    return ord.medicationName === medName
                        ? { ...ord, doctorName: docName }
                        : ord;
                }),
            }));
        },
        [setValues]
    );

    const handleAutocompleteChange = useCallback(
        (
            index: number,
            value: string[],
            selectedMeds: string[][],
            setSelectedMeds: React.Dispatch<React.SetStateAction<string[][]>>
        ) => {
            const otherSelectedMeds = selectedMeds.flatMap((meds, idx) =>
                idx !== index ? meds : []
            );
            const allSelectedMeds = [...otherSelectedMeds, ...value];

            const doc = doctors[index];
            const docName = doc.mname
                ? `${doc.fname} ${doc.mname} ${doc.lname}`
                : `${doc.fname} ${doc.lname}`;

            if (value.length === 0) {
                updateOrderData('', docName);
            } else {
                value.forEach((med) => updateOrderData(med, docName));
            }

            const updated = [...selectedMeds];
            updated[index] = value;
            setSelectedMeds(updated);

            checkAllMedsSelected(allSelectedMeds);
        },
        [doctors, updateOrderData, checkAllMedsSelected]
    );

    const syncFromOrders = useCallback(
        (
            setSelectedMeds: React.Dispatch<React.SetStateAction<string[][]>>
        ) => {
            if (hasInitialized.current) return;

            const docNames = doctors.map((doc) =>
                doc.mname
                    ? `${doc.fname} ${doc.mname} ${doc.lname}`
                    : `${doc.fname} ${doc.lname}`
            );

            const groupedMeds = docNames.map((name) =>
                orders
                    .filter((order: any) => order.doctorName === name)
                    .map((order: any) => order.medicationName)
            );

            if (groupedMeds.every((list) => list.length === 0)) return;

            setSelectedMeds(groupedMeds);
            hasInitialized.current = true;
        },
        [doctors, orders]
    );

    return {
        handleAutocompleteChange,
        syncFromOrders,
    };
}